/**
 * Camada única de acesso à API.
 *
 * Em desenvolvimento, VITE_API_URL fica vazia e as chamadas para `/api`
 * passam pelo proxy do Vite (veja vite.config.ts), que redireciona para o
 * backend em http://localhost:3333. Em produção, defina VITE_API_URL.
 *
 * O token JWT do criador logado fica no localStorage e é injetado
 * automaticamente no cabeçalho Authorization das rotas protegidas.
 */
const BASE_URL = import.meta.env.VITE_API_URL ?? '';

const TOKEN_KEY = 'send-message:token';

// ---- Sessão (token JWT) ----

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function saveToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

// ---- Tipos compartilhados com a API ----

export interface User {
  id: string;
  name: string;
  email: string;
  username: string;
  createdAt: string;
  /** Administrador da plataforma (vem de ADMIN_EMAILS no servidor). */
  isAdmin?: boolean;
}

export type MessageStatus = 'new' | 'read' | 'hidden';

/** Plataforma onde o espectador se autenticou ao enviar (selo "via ..."). */
export type SenderPlatform = 'twitch' | 'youtube' | 'kick';

export interface DashboardMessage {
  id: string;
  senderName: string;
  message: string;
  amountCents: number;
  status: MessageStatus;
  createdAt: string;
  senderPlatform?: SenderPlatform | null;
}

export interface MessagesPage {
  messages: DashboardMessage[];
  pagination: { page: number; perPage: number; total: number; totalPages: number };
}

export interface FinanceSummary {
  balanceCents: number;
  total: { amountCents: number; messageCount: number };
  today: { amountCents: number; messageCount: number };
  recentTransactions: DashboardMessage[];
}

export interface PublicCreator {
  name: string;
  username: string;
}

/**
 * Erro lançado quando a API responde com status de erro.
 * - `code`: identificador estável (ex.: 'INVALID_CREDENTIALS') para tratar
 *   erros sem depender do texto da mensagem
 * - `requestId`: id da requisição (X-Request-Id) — mostre/registre em caso de
 *   erro inesperado para facilitar o rastreio nos logs do backend
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly issues?: { field: string; error: string }[],
    public readonly code?: string,
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ---- Núcleo de requisições ----

async function request<T>(path: string, options: RequestInit = {}, withAuth = false): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (withAuth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      message?: string;
      code?: string;
      requestId?: string;
      issues?: { field: string; error: string }[];
    } | null;
    throw new ApiError(
      response.status,
      body?.message ?? `Erro na requisição (HTTP ${response.status})`,
      body?.issues,
      body?.code,
      body?.requestId,
    );
  }

  return response.json() as Promise<T>;
}

// ---- Autenticação do criador ----

export interface RegisterPayload {
  name: string;
  email: string;
  username: string;
  password: string;
}

export function register(payload: RegisterPayload): Promise<{ token: string; user: User }> {
  return request('/api/auth/register', { method: 'POST', body: JSON.stringify(payload) });
}

export function login(email: string, password: string): Promise<{ token: string; user: User }> {
  return request('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export function fetchMe(): Promise<{ user: User }> {
  return request('/api/me', {}, true);
}

// ---- Página pública de envio (espectador) ----

export function fetchCreator(username: string): Promise<PublicCreator> {
  return request(`/api/creators/${encodeURIComponent(username)}`);
}

export interface SendMessagePayload {
  /** Vazio + sem viewerToken = nome aleatório gerado pelo servidor. */
  senderName?: string;
  /** Identidade verificada do login social (tem prioridade sobre senderName). */
  viewerToken?: string;
  message: string;
  amountCents: number;
}

export function sendMessage(
  username: string,
  payload: SendMessagePayload,
): Promise<DashboardMessage> {
  return request(`/api/creators/${encodeURIComponent(username)}/messages`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ---- Identificação do espectador (login social) ----

export interface ViewerProvider {
  id: SenderPlatform;
  label: string;
}

export function fetchViewerProviders(): Promise<{ providers: ViewerProvider[] }> {
  return request('/api/viewer-auth/providers');
}

/** URL que inicia o login social (redirecionamento de página inteira). */
export function viewerAuthStartUrl(provider: SenderPlatform, creatorUsername: string): string {
  return `${BASE_URL}/api/viewer-auth/${provider}/start?creator=${encodeURIComponent(creatorUsername)}`;
}

const VIEWER_TOKEN_KEY = 'send-message:viewer-token';

export interface ViewerIdentity {
  name: string;
  platform: SenderPlatform;
}

export function getViewerToken(): string | null {
  return localStorage.getItem(VIEWER_TOKEN_KEY);
}

export function saveViewerToken(token: string): void {
  localStorage.setItem(VIEWER_TOKEN_KEY, token);
}

export function clearViewerToken(): void {
  localStorage.removeItem(VIEWER_TOKEN_KEY);
}

/**
 * Decodifica o payload do viewer token SÓ para exibição ("enviando como X").
 * A validação de verdade acontece no backend, que verifica a assinatura.
 */
export function decodeViewerIdentity(token: string): ViewerIdentity | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? '')) as {
      typ?: string;
      name?: string;
      platform?: string;
      exp?: number;
    };
    if (payload.typ !== 'viewer' || !payload.name || !payload.platform) return null;
    if (payload.exp && payload.exp * 1000 < Date.now()) return null;
    return { name: payload.name, platform: payload.platform as SenderPlatform };
  } catch {
    return null;
  }
}

// ---- Dashboard do criador (rotas protegidas) ----

export function fetchDashboardMessages(
  status: MessageStatus | 'all' = 'all',
  page = 1,
): Promise<MessagesPage> {
  const query = new URLSearchParams({ status, page: String(page) });
  return request(`/api/dashboard/messages?${query}`, {}, true);
}

export function updateMessageStatus(id: string, status: MessageStatus): Promise<DashboardMessage> {
  return request(
    `/api/dashboard/messages/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify({ status }) },
    true,
  );
}

export function fetchFinance(): Promise<FinanceSummary> {
  return request('/api/dashboard/finance', {}, true);
}

// ---- Overlay do OBS ----

export interface OverlaySettings {
  /** Segundos que cada alerta fica na tela da live (3 a 60). */
  durationSeconds: number;
  /** Valor mínimo (centavos) para a mensagem aparecer na live; 0 = todas. */
  minAmountCents: number;
}

export type OverlayTemplate =
  'classic' | 'minimal' | 'banner' | 'neon' | 'bubble' | 'pill' | 'retro' | 'seal' | 'custom';

export type OverlayPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'middle-left'
  | 'middle-center'
  | 'middle-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

/** Tema/aparência do alerta do overlay (padrão: clássico no rodapé). */
export interface OverlayTheme {
  template: OverlayTemplate;
  position: OverlayPosition;
  accentColor: string;
  backgroundColor: string;
  textColor: string;
  logoUrl: string;
  customHtml: string;
}

export interface OverlayConfig {
  overlayToken: string;
  settings: OverlaySettings;
  theme: OverlayTheme;
}

/** Alerta recebido pela página /overlay/:token via SSE. */
export interface OverlayAlert {
  id: string;
  senderName: string;
  message: string;
  amountCents: number;
  createdAt: string;
  durationSeconds: number;
  isTest?: boolean;
  senderPlatform?: SenderPlatform | null;
  /** Tema resolvido no momento da publicação (aparência sempre atual). */
  theme?: OverlayTheme;
}

export function fetchOverlayConfig(): Promise<OverlayConfig> {
  return request('/api/dashboard/overlay', {}, true);
}

export function updateOverlaySettings(
  settings: OverlaySettings & { theme?: OverlayTheme },
): Promise<OverlayConfig> {
  return request(
    '/api/dashboard/overlay',
    { method: 'PATCH', body: JSON.stringify(settings) },
    true,
  );
}

export function regenerateOverlayToken(): Promise<OverlayConfig> {
  return request('/api/dashboard/overlay/regenerate', { method: 'POST' }, true);
}

export function sendOverlayTestAlert(): Promise<{ ok: boolean }> {
  return request('/api/dashboard/overlay/test', { method: 'POST' }, true);
}

/** URL do fluxo SSE do overlay (usada pela página /overlay/:token). */
export function overlayStreamUrl(token: string): string {
  return `${BASE_URL}/api/overlay/${encodeURIComponent(token)}/stream`;
}

// ---- Página do moderador (/mod/:token) ----

export interface ModInitialState {
  creator: { name: string; username: string };
  messages: DashboardMessage[];
}

export function fetchModMessages(token: string): Promise<ModInitialState> {
  return request(`/api/mod/${encodeURIComponent(token)}/messages`);
}

export function updateModMessageStatus(
  token: string,
  id: string,
  status: MessageStatus,
): Promise<DashboardMessage> {
  return request(`/api/mod/${encodeURIComponent(token)}/messages/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

/** URL do fluxo SSE da página do moderador. */
export function modStreamUrl(token: string): string {
  return `${BASE_URL}/api/mod/${encodeURIComponent(token)}/stream`;
}

// ---- Integrações (webhooks, Twitch) ----

export type WebhookFormat = 'json' | 'discord';

export interface Webhook {
  id: string;
  url: string;
  format: WebhookFormat;
  enabled: boolean;
  createdAt: string;
}

export interface TwitchIntegration {
  enabled: boolean;
  channel: string;
  botUsername: string;
  /** O token nunca volta do servidor — só a informação de que existe. */
  hasToken: boolean;
}

export interface IntegrationsConfig {
  modToken: string;
  webhooks: Webhook[];
  twitch: TwitchIntegration;
}

export function fetchIntegrations(): Promise<IntegrationsConfig> {
  return request('/api/dashboard/integrations', {}, true);
}

export function regenerateModToken(): Promise<IntegrationsConfig> {
  return request('/api/dashboard/integrations/mod/regenerate', { method: 'POST' }, true);
}

export function addWebhook(url: string, format: WebhookFormat): Promise<IntegrationsConfig> {
  return request(
    '/api/dashboard/webhooks',
    { method: 'POST', body: JSON.stringify({ url, format }) },
    true,
  );
}

export function deleteWebhook(id: string): Promise<IntegrationsConfig> {
  return request(`/api/dashboard/webhooks/${encodeURIComponent(id)}`, { method: 'DELETE' }, true);
}

export function testWebhooks(): Promise<{ ok: boolean }> {
  return request('/api/dashboard/webhooks/test', { method: 'POST' }, true);
}

export interface TwitchConfigPayload {
  enabled: boolean;
  channel: string;
  botUsername: string;
  /** Vazio mantém o token já salvo no servidor. */
  oauthToken: string;
}

export function saveTwitchIntegration(payload: TwitchConfigPayload): Promise<IntegrationsConfig> {
  return request(
    '/api/dashboard/integrations/twitch',
    { method: 'PUT', body: JSON.stringify(payload) },
    true,
  );
}

export function testTwitchIntegration(): Promise<{ ok: boolean }> {
  return request('/api/dashboard/integrations/twitch/test', { method: 'POST' }, true);
}

// ---- Painel de administração da plataforma ----
//
// Todas as rotas exigem uma conta marcada como admin no servidor
// (ADMIN_EMAILS). O `user.isAdmin` do login serve só para decidir o que
// MOSTRAR — quem autoriza de verdade é a API.

/** Janelas de tempo oferecidas no painel. */
export type AdminRange = '15m' | '1h' | '6h' | '24h' | '7d';

/** Recorte de tráfego: tudo, só gente de verdade, ou só o teste de carga. */
export type AdminTraffic = 'all' | 'real' | 'synthetic';

export interface AdminFilters {
  range: AdminRange;
  traffic: AdminTraffic;
}

/** Um segundo (ou balde) da série temporal de tráfego. */
export interface TrafficPoint {
  time: string;
  total: number;
  ok: number;
  clientError: number;
  serverError: number;
  visitors?: number;
  synthetic?: number;
  p50Ms?: number;
  p95Ms?: number;
  p99Ms?: number;
  avgDurationMs?: number;
  maxDurationMs?: number;
  requestsPerSecond?: number;
}

/** Métricas em tempo real, medidas na memória do processo da API. */
export interface LiveMetrics {
  windowSeconds: number;
  requestsPerSecond: number;
  totalRequests: number;
  errorRate: number;
  p50Ms: number;
  p95Ms: number;
  p99Ms: number;
  maxDurationMs: number;
  syntheticShare: number;
  series: TrafficPoint[];
  droppedWrites: number;
  liveConnections: { overlay: number; moderator: number; total: number };
  databasePool: { total: number; idle: number; waiting: number };
  memoryRssMb: number;
  security: {
    rateLimitedTotal: number;
    sseRejectedTotal: number;
    sseConnections: number;
    trackedIps: number;
  };
}

/** Um IP no ranking de bloqueados pela blindagem. */
export interface OffenderRow {
  ip: string;
  blocks: number;
}

/** Estado da blindagem contra abuso e DDoS. */
export interface SecurityState {
  range: AdminRange;
  live: {
    rateLimitedTotal: number;
    sseRejectedTotal: number;
    sseConnections: number;
    trackedIps: number;
    topOffenders: OffenderRow[];
  };
  period: {
    blockedTotal: number;
    byRoute: { route: string; blocked: number }[];
    byIp: { ip: string | null; blocked: number; location: string | null }[];
  };
}

export interface AdminOverview {
  range: AdminRange;
  traffic: AdminTraffic;
  live: Omit<LiveMetrics, 'liveConnections' | 'databasePool' | 'memoryRssMb'>;
  period: {
    requests: number;
    visitors: number;
    errors: number;
    serverErrors: number;
    syntheticRequests: number;
    botRequests: number;
    errorRate: number;
    p50Ms: number;
    p95Ms: number;
    p99Ms: number;
    avgMs: number;
  };
  system: {
    uptimeSeconds: number;
    nodeVersion: string;
    loadAverage: number[];
    cpuCount: number;
    memory: {
      rssMb: number;
      heapUsedMb: number;
      heapTotalMb: number;
      systemFreeMb: number;
      systemTotalMb: number;
    };
    databasePool: { total: number; idle: number; waiting: number };
    liveConnections: { overlay: number; moderator: number; total: number };
    pendingGeoLookups: number;
  };
  business: {
    creators: number;
    messages: number;
    revenueCents: number;
    messagesToday: number;
    revenueTodayCents: number;
    syntheticMessages: number;
  };
}

export interface RouteStat {
  method: string;
  route: string;
  requests: number;
  errors: number;
  visitors: number;
  errorRate: number;
  p50Ms: number;
  p95Ms: number;
  p99Ms: number;
  maxMs: number;
}

export interface GeoBreakdown {
  countries: {
    country: string;
    countryCode: string | null;
    requests: number;
    visitors: number;
  }[];
  cities: {
    city: string | null;
    region: string | null;
    country: string | null;
    latitude: number | null;
    longitude: number | null;
    requests: number;
    visitors: number;
  }[];
  organizations: { organization: string | null; requests: number }[];
  loadTestOrigins: { origin: string | null; requests: number; visitors: number }[];
}

/** Uma linha de qualquer agrupamento simples (navegador, sistema, referer...). */
export interface LabelCount {
  label: string;
  requests: number;
  visitors: number;
}

export interface DeviceBreakdown {
  browsers: LabelCount[];
  operatingSystems: LabelCount[];
  devices: LabelCount[];
  referers: LabelCount[];
}

export interface VisitorStat {
  ip: string | null;
  requests: number;
  errors: number;
  routes: number;
  firstSeen: string;
  lastSeen: string;
  city: string | null;
  country: string | null;
  countryCode: string | null;
  organization: string | null;
  isSynthetic: boolean;
}

export interface AccessEntry {
  createdAt: string;
  requestId: string | null;
  method: string;
  route: string;
  status: number;
  durationMs: number;
  ip: string | null;
  browser: string | null;
  os: string | null;
  device: string | null;
  isSynthetic: boolean;
  origin: string | null;
  city: string | null;
  country: string | null;
  countryCode: string | null;
}

export interface CreatorStat {
  id: string;
  name: string;
  username: string;
  createdAt: string;
  messages: number;
  revenueCents: number;
  requests: number;
  visitors: number;
}

export interface LoadTestFootprint {
  syntheticAccessLogs: number;
  syntheticMessages: number;
  firstSeen: string | null;
  lastSeen: string | null;
}

function adminQuery({ range, traffic }: AdminFilters): string {
  return `range=${range}&traffic=${traffic}`;
}

export function fetchAdminOverview(filters: AdminFilters): Promise<AdminOverview> {
  return request(`/api/admin/overview?${adminQuery(filters)}`, {}, true);
}

/** Janela ao vivo (memória da API) — barata o bastante para consultar a cada segundo. */
export function fetchAdminLive(seconds = 60): Promise<LiveMetrics> {
  return request(`/api/admin/live?seconds=${seconds}`, {}, true);
}

export function fetchAdminTraffic(
  filters: AdminFilters,
): Promise<{ range: AdminRange; bucketSeconds: number; series: TrafficPoint[] }> {
  return request(`/api/admin/traffic?${adminQuery(filters)}`, {}, true);
}

export function fetchAdminRoutes(filters: AdminFilters): Promise<{ routes: RouteStat[] }> {
  return request(`/api/admin/routes?${adminQuery(filters)}`, {}, true);
}

export function fetchAdminGeo(filters: AdminFilters): Promise<GeoBreakdown> {
  return request(`/api/admin/geo?${adminQuery(filters)}`, {}, true);
}

export function fetchAdminDevices(filters: AdminFilters): Promise<DeviceBreakdown> {
  return request(`/api/admin/devices?${adminQuery(filters)}`, {}, true);
}

export function fetchAdminVisitors(filters: AdminFilters): Promise<{ visitors: VisitorStat[] }> {
  return request(`/api/admin/visitors?${adminQuery(filters)}`, {}, true);
}

export function fetchAdminAccesses(
  filters: AdminFilters,
  options: { limit?: number; onlyErrors?: boolean } = {},
): Promise<{ accesses: AccessEntry[] }> {
  const extra = `&limit=${options.limit ?? 50}&onlyErrors=${options.onlyErrors ? 'true' : 'false'}`;
  return request(`/api/admin/accesses?${adminQuery(filters)}${extra}`, {}, true);
}

export function fetchAdminCreators(filters: AdminFilters): Promise<{ creators: CreatorStat[] }> {
  return request(`/api/admin/creators?${adminQuery(filters)}`, {}, true);
}

export function fetchLoadTestFootprint(): Promise<LoadTestFootprint> {
  return request('/api/admin/load-test', {}, true);
}

export function fetchAdminSecurity(filters: AdminFilters): Promise<SecurityState> {
  return request(`/api/admin/security?${adminQuery(filters)}`, {}, true);
}

/** Apaga o que o teste de carga deixou. Marcado na origem — não toca em dado real. */
export function cleanupLoadTestData(
  target: 'messages' | 'accesses' | 'all',
): Promise<{ deletedMessages: number; deletedAccessLogs: number }> {
  return request(
    '/api/admin/load-test/cleanup',
    { method: 'POST', body: JSON.stringify({ target }) },
    true,
  );
}
